import { Alert, ButtonDropdown, Input, SpaceBetween, Table } from '@amzn/awsui-components-console';
import React, { useEffect, useState } from 'react';
import { useNavigate, useMatch } from 'react-router';

import { removeFromRecentPages } from 'src/commons';
import keys from 'src/constants/hammerstoneConstantKeys';
import { fetchPipelineNames } from 'src/data/api/fetchFromAPI';
import { actions, useGroupName, usePipeline } from 'src/data/redux';
import { DeletePipelineInfo } from 'src/lib/hammerstoneApi';
import { urlSearchString } from 'src/nav/navHelper';
import PAGES from 'src/nav/Pages';
import { ConfirmActionModal, CustomStatusIndicator, tempFlashbarMessage } from '../helpers';
import { ActivityInfo } from 'src/interfaces/pipelineInterfaces';

enum Action {
	View = 'view',
	Add = 'add',
	Edit = 'edit',
	Delete = 'delete',
}

const activityTableColumns = [
	{
		id: 'activityId',
		header: 'Activity ID',
		cell: ({ activityId }: ActivityInfo) => activityId,
	},
	{
		id: 'activityName',
		header: 'Activity name',
		cell: ({ activityName }: ActivityInfo) => activityName,
	},
	{
		id: 'status',
		header: 'Status',
		cell: ({ status }: ActivityInfo) => <CustomStatusIndicator>{status}</CustomStatusIndicator>,
	},
];

export default function PipelineActionsDropdown(props: { pipelineName: string }) {
	const { pipelineName } = props;

	const matchesViewPipeline = useMatch(PAGES.VIEW_PIPELINE.path);
	const isViewPipelinePage = matchesViewPipeline !== null;

	const groupName = useGroupName();
	const navigate = useNavigate();

	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [deleteConfirmText, setDeleteConfirmText] = useState('');
	// The current action, set to empty string if no action is currently being processed
	const { data: pipeline, fetching } = usePipeline({ groupName, pipelineName });
	const [isCalling, setIsCalling] = useState(false);

	const { status } = pipeline || {};
	const activities = pipeline?.activityInfoList || [];

	// Reset confirmation text when modal closes
	useEffect(() => {
		if (!deleteModalVisible) {
			setDeleteConfirmText('');
		}
	}, [deleteModalVisible]);

	// Button dropdown is disabled if no pipeline name is set, or if no pipeline has loaded
	const disabled = !pipelineName || !pipeline;
	// If not disabled, set to loading state if object is fetching or the action is non-empty
	const loading = fetching || isCalling;

	const items: any[] = [
		// When you are NOT on the view pipeline page, "View pipeline" will be the main action
		// and "Add activity" will be a secondary action item in the dropdown.
		// When you ARE on the view pipeline page, "Add activity" becomes the main (and primary) action
		// and therefore is not repeated in the dropdown
		...(isViewPipelinePage ? [] : [{ text: 'Add activity', id: Action.Add, iconName: 'add-plus' }]),
		{
			text: 'Edit pipeline',
			id: Action.Edit,
			iconName: 'edit',
		},
		{
			text: 'Delete pipeline',
			id: Action.Delete,
			iconName: 'remove',
			disabled: status === keys.StatusCode.EDIT_PENDING,
			disabledReason: 'Pipeline cannot be deleted while an edit is pending',
		},
	];

	function doAction(action: Action) {
		switch (action) {
			case Action.Delete:
				return setDeleteModalVisible(true);
			case Action.View:
				return navigate({
					pathname: PAGES.VIEW_PIPELINE.path.replace(':pipelineName', pipelineName),
					search: urlSearchString({ groupName }),
				});
			case Action.Add:
				return navigate({
					pathname: PAGES.CREATE_ACTIVITY.path,
					search: urlSearchString({ groupName, pipelineName }),
				});
			case Action.Edit:
				return navigate({
					pathname: PAGES.EDIT_PIPELINE.path.replace(':pipelineName', pipelineName),
					search: urlSearchString({ groupName }),
				});
		}
	}

	return (
		<>
			<ConfirmActionModal
				visible={deleteModalVisible}
				setVisible={setDeleteModalVisible}
				header={`Permanently delete pipeline '${pipelineName}'?`}
				confirmName={'Delete'}
				size="large"
				disabled={activities.length > 0 && deleteConfirmText !== 'delete'}
				onConfirm={async () => {
					setIsCalling(true);
					try {
						await DeletePipelineInfo({
							groupName,
							pipelineName,
						});
						setIsCalling(false);
						tempFlashbarMessage({
							id: `delete_pipeline_${pipelineName}`,
							message: { header: `Successfully deleted pipeline '${pipelineName}'!` },
						});
						fetchPipelineNames({ groupName }, true);

						// Avoids outdated reference to deleted pipeline on the Home page
						removeFromRecentPages('pipeline', pipelineName);

						navigate({ pathname: PAGES.MANAGE_PIPELINES.path, search: urlSearchString({ groupName }) });
					} catch (error) {
						actions.page.addToFlashbar({
							id: `pipeline_delete_failed`,
							message: {
								type: 'error',
								header: `Failed to delete pipeline '${pipelineName}'`,
								content: error.toString(),
								dismissible: true,
							},
						}),
							console.error(error);
					}
				}}
			>
				{activities.length > 0 ? (
					<SpaceBetween size="m">
						<Alert type="warning" header="Permanent action">
							Are you sure you want to delete this pipeline? Doing so will permanently delete all its
							activities (listed below) and cannot be undone.
						</Alert>
						<Table
							columnDefinitions={activityTableColumns}
							items={activities}
							variant="embedded"
							empty="No activities found"
						/>
						<div>
							If you are sure you would like to proceed with deleting pipeline{' '}
							<strong>{pipelineName}</strong> and all <strong>{activities.length}</strong> of its{' '}
							{activities.length === 1 ? 'activity' : 'activities'}, type "delete" below and then click
							the Delete button to confirm.
						</div>
						<Input
							value={deleteConfirmText}
							onChange={({ detail }) => setDeleteConfirmText(detail.value)}
							placeholder='Type "delete" to confirm'
							ariaLabel="Confirm deletion"
						/>
					</SpaceBetween>
				) : (
					<>
						Are you sure you would like to permanently delete pipeline <strong>{pipelineName}</strong>?
					</>
				)}
			</ConfirmActionModal>
			<ButtonDropdown
				data-testid="managePipelinesActions"
				disabled={disabled}
				loading={loading}
				items={items}
				onItemClick={async ({ detail }) => doAction(detail.id as Action)}
				variant={isViewPipelinePage ? 'primary' : 'normal'}
				mainAction={{
					onClick: () => doAction(!isViewPipelinePage ? Action.View : Action.Add),
					text: isViewPipelinePage ? 'Add activity' : 'View pipeline',
					iconName: isViewPipelinePage ? 'add-plus' : 'zoom-in',
					loading,
					disabled,
				}}
			/>
		</>
	);
}
